import styled from '@emotion/styled';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { ChevronIcon } from 'src/domains/layouts/shared/assets/icons/ChevronIcon';
import { Link } from 'src/domains/layouts/webview/components/router/NewLink';
import { InfoContent } from 'src/domains/players/webview/components/SignUp/signupParts/termsAndConditions/TermsAndConditions.style';
import { withConfig } from 'src/withConfig';

const blockingEditLimitsColumnMaxWidth = '600px';

export const OverlayDefault = withConfig(theme => styled('div', { label: 'OverlayDefault' })`
    align-items: flex-start;
    background: ${theme.star.utilityPage.bgColor};
    color: ${theme.star.utilityPage.txtColor};
    display: flex;
    inset: 0;
    justify-content: center;
    overflow-y: auto;
    padding: 24px;
    position: fixed;
    scrollbar-color: ${theme.star.scrollbar.dark.bgColor} transparent;
    scrollbar-width: thin;
    z-index: ${theme.star.zIndexGlobal.highLevel};
    ::-webkit-scrollbar {
        width: 8cqmax;
    }
    ::-webkit-scrollbar-track {
        background: transparent;
    }
    @media ${theme.star.mediaQuery.tablet} {
        padding: 16px;
    }
`);

/** Deposit limits blocking flow: centre panel on both axes (default overlay uses align-items: flex-start). */
export const BlockingEditLimitsOverlay = withConfig(theme => styled(OverlayDefault, { label: 'BlockingEditLimitsOverlay' })`
    flex-direction: column;
    justify-content: flex-start;
    @media ${theme.star.mediaQuery.tablet} {
        padding: 104px 16px;
    }
`);

export const Content = styled('div', { label: 'Content' })`
    margin: auto;
    max-width: 480px;
    position: relative;
    text-align: center;
    width: 100%;
`;

export const PopupHeader = withConfig(theme => styled('h2', { label: 'PopupHeader' })`
    font-size: ${theme.star.fontSize.medium};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.4;
    margin: 24px 0 0 0;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.medium};
        line-height: 1.3334;
    }
`);

export const PopupHeaderSaferGambling = styled(PopupHeader, { label: 'PopupHeaderSaferGambling' })`
    margin: 48px 0 0 0;
`;

export const PopupHeaderSaferGamblingClamped = styled(PopupHeaderSaferGambling, { label: 'PopupHeaderSaferGamblingClamped' })`
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    -webkit-line-clamp: 5;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
`;

export const MarkedTxt = withConfig(theme => styled('a', { label: 'MarkedTxt' })`
    color: ${ theme.star.utilityPage.txtColorTertiary};
    text-decoration: underline;
`);

export const ContentRingFencedFunds = styled(Content, { label: 'ContentRingFencedFunds' })`
    max-width: 420px;
`;

export const ContentSaferGambling = styled(Content, { label: 'ContentSaferGambling' })`
    max-width: 500px;
`;

export const BlockingEditLimitsBody = styled('div', { label: 'BlockingEditLimitsBody' })<{ type: 'review' | 'edit' }>`
    margin-top: 8px;
    text-align: left;
    width: 100%;

    ${({ type }) =>type === 'edit' ? `
        display: flex;
        flex-direction: column;
        flex: 1;
    ` :''}
`;

export const BlockingEditLimitsLoaderArea = styled('div', { label: 'BlockingEditLimitsLoaderArea' })`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    min-height: 168px;
    padding: 24px 0;
    width: 100%;
`;

export const ContentEditLimits = styled(Content, { label: 'ContentEditLimits' })`
    align-self: center;
    margin: 0;
    max-width: ${blockingEditLimitsColumnMaxWidth};
    flex: 1;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

/** Single top row: review — logo right; edit — back control left. */
export const BlockingEditLimitsTopHeader = withConfig(theme => styled('header', { label: 'BlockingEditLimitsTopHeader' })`
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-shrink: 0;
    gap: ${theme.star.padding.medium};
    justify-content: space-between;
    margin-bottom: 8px;
    min-height: 44px;
    width: 100%;
    @media ${theme.star.mediaQuery.tablet} {
        margin-top: -16px;
    }
`);

export const BlockingEditLimitsTopHeaderLead = styled('div', { label: 'BlockingEditLimitsTopHeaderLead' })`
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: flex-start;
    min-height: 0;
    min-width: 0;
`;

/** Same outer column as `ContentEditLimits` (`blockingEditLimitsColumnMaxWidth`). */
export const BlockingEditLimitsHeaderLogoSlot = styled('div', { label: 'BlockingEditLimitsHeaderLogoSlot' })<{ isCentered?: boolean }>`
    align-items: center;
    align-self: center;
    box-sizing: border-box;
    display: flex;
    flex-shrink: 0;
    justify-content: ${({ isCentered }): string => (isCentered === true ? 'center' : 'flex-start')};
    line-height: 0;
    max-width: ${blockingEditLimitsColumnMaxWidth};
    width: 100%;
`;

/** Review mockup: title centred with logo. */
export const BlockingEditLimitsReviewTitleWrap = styled('div', { label: 'BlockingEditLimitsReviewTitleWrap' })`
    align-items: center;
    display: flex;
    margin-bottom: 8px;
    flex-direction: column;
    text-align: center;
    width: 100%;

    & > h2 {
        text-align: center;
        width: 100%;
    }
`;

export const BlockingEditLimitsReviewIntro = styled('div', { label: 'BlockingEditLimitsReviewIntro' })`
    width: 100%;
    text-align: left;
`;

export const BlockingEditLimitsBackRow = withConfig(theme => styled('button', { label: 'BlockingEditLimitsBackRow' })`
    align-items: center;
    appearance: none;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    gap: 2px;
    font-family: inherit;
    font-size: ${theme.star.fontSize.regular};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3;
    margin: 0;
    padding: 0;
    text-align: left;
`);

/** Desktop: compact chevron; tablet/mobile mockup: 24×24. */
export const BlockingEditLimitsBackChevron = withConfig(theme => styled(ChevronIcon, { label: 'BlockingEditLimitsBackChevron' })`
    fill: currentcolor;
    flex-shrink: 0;
    height: 14px;
    margin-left: 0;
    padding: 0;
    width: 14px;
    @media ${theme.star.mediaQuery.tablet} {
        height: 24px;
        margin-left: -8px;
        width: 24px;
    }
`);

export const BackTextWrapper = styled('span', { label: 'BackTextWrapper' })`
    margin-left: 8px;
`;
export const BlockingEditLimitsTitle = withConfig(theme => styled('h2', { label: 'BlockingEditLimitsTitle' })`
    color: ${theme.star.utilityPage.txtColor};
    font-size: ${theme.star.fontSize.medium};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.4;
    margin: 12px 0 0 0;
    text-align: left;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.medium};
        line-height: 1.3334;
    }
`);

export const RtpWrapper = styled(Content, { label: 'RtpWrapper' })`
    text-align: left;
    margin-top: 24px;
    max-width: 420px;
`;

export const PopupDescription = withConfig(theme => styled('p', { label: 'PopupDescription' })`
    color: ${theme.star.utilityPage.txtColorTertiary};
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
    margin: 16px 0;
    &:first-of-type {
        margin-top: 8px;
    }
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xRegular};
        line-height: 1.4286;
    }
`);

export const PopupDescriptionNoHeadline = styled(PopupDescription, { label: 'PopupDescriptionNoHeadline' })`
    &:first-of-type {
        margin-top: 48px;
    }
`;

interface TermsBoxPropsType {
    systemPagesVariant: DefaultComponentVariantType | null;
}

export const TermsBox = withConfig(theme => styled('div', { label: 'TermsBox' })<TermsBoxPropsType>`
    background: ${theme.star.utilityPage.bgColorSecondary};
    color: ${theme.star.utilityPage.txtColorTertiary};
    font-size: ${theme.star.fontSize.small};
    height: 184px;
    line-height: 1.3334;
    margin: 16px 0;
    padding: 0 4px 0 0;
    position: relative;
    border-radius: ${({ systemPagesVariant }): string => systemPagesVariant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'};
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xRegular};
        line-height: 1.4286;
    }
    > div {
        hyphens: auto;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-color: ${theme.star.buttons.primary.bgColorSecondary} transparent;
        scrollbar-width: thin;
        text-align: justify;
        ::-webkit-scrollbar {
            width: 4px;
        }
        ::-webkit-scrollbar-track {
            background: transparent;
        }
        ::-webkit-scrollbar-thumb {
            background: ${theme.star.buttons.primary.bgColorSecondary};
        }
    }
    > p {
        line-height: 1.3334;
    }
`);

export const AcceptBtn = withConfig(theme => styled(Button, { label: 'AcceptBtn' })`
    margin-bottom: 4px;
    width: 100%;
    @media ${theme.star.mediaQuery.tablet} {
        margin-top: 8px;
    }
`);

export const AcceptBtnContact = styled(Button, { label: 'AcceptBtnContact' })`
    width: 100%;
    margin-top: 10px;
`;

export const LinkWrapper = withConfig(theme => styled(Link, { label: 'LinkWrapper' })`
    color: ${theme.star.blockingPopUp.txtColor};
    cursor: pointer;
    font-weight: ${theme.star.fontWeight.bold};
    text-decoration: none;
    position: relative;
    &::after {
        background: currentcolor;
        content: '';
        height: 1px;
        left: 0;
        opacity: 0;
        position: absolute;
        right: 0;
        top: 100%;
        transition: opacity 0.2s ease;
    }
    &:hover {
        &::after {
            opacity: 1;
        }
    }
`);

export const SaferGamblingText = withConfig(theme => styled('div', { label: 'SaferGamblingText' })`
    color: ${theme.star.blockingPopUp.txtColorSecondary};
    display: inline-block;
`);

export const FaqModalScroll = styled(InfoContent, { label: 'FaqModalScroll' })`
    height: auto;
    max-height: 60vh;
    overflow-y: auto;
`;

export const ReviewSaferGamblingBtn = withConfig(theme => styled(Button, { label: 'ReviewSaferGamblingBtn' })`
    background-color: ${theme.star.blockingPopUp.secondaryBtnBgColor};
    color: ${theme.star.blockingPopUp.secondaryBtnTxtColor};
    margin-top: 8px;
    width: 100%;
`);

export const SaferGamblingActionsDisclaimer = withConfig(theme => styled('p', { label: 'SaferGamblingActionsDisclaimer' })`
    color: ${theme.star.blockingPopUp.disclaimerTxtColor};
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.regular};
    line-height: 1.2;
    margin: 12px 0 0;
    text-align: center;
`);

export const TermsBoxStaticPageContent = withConfig(theme => styled('div', { label: 'TermsBoxStaticPageContent' })`
    background: ${theme.star.utilityPage.bgColorSecondary};
    color: ${theme.star.utilityPage.txtColorTertiary};
    font-size: ${theme.star.fontSize.small};
    height: 60dvh;
    line-height: 1.3334;
    margin: 16px 0;
    padding: 0 4px 0 0;
    position: relative;
    @media ${theme.star.mediaQuery.tablet} {
        font-size: ${theme.star.fontSize.xRegular};
        line-height: 1.4286;
    }
    > div {
        hyphens: auto;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-color: ${theme.star.buttons.primary.bgColorSecondary} transparent;
        scrollbar-width: thin;
        text-align: justify;
        ::-webkit-scrollbar {
            width: 4px;
        }
        ::-webkit-scrollbar-track {
            background: transparent;
        }
        ::-webkit-scrollbar-thumb {
            background: ${theme.star.buttons.primary.bgColorSecondary};
        }
    }
    > p {
        line-height: 1.3334;
    }
`
);
